import { useMutation } from '@tanstack/react-query';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { AssistQueryClient, useQueryAssist } from 'services/assist-api/query-client';
import { prepareTestInstance } from './test-instance.prepare';
import type { BaseTestInstance } from './types';

const order = { 'order:[startdate]': 'desc', 'order:[name]': 'asc' };
const groups = [
  'testinstance-name',
  'testinstance-startdate',
  'testinstance-enddate',
  'testinstance-responsescount',
  'testinstance-test',
  'test-tokencreationlimit',
  'test-name',
  'test-notes',
  'testinstance-area',
  'area-type',
  'area-identifier',
  'area-name',
  'enabled'
];

const include = 'test,area';
const includeExtended = 'test,area,level,referenceLevel,customer';

/**
 * Fetch test-instances
 * @param params can be used to customise/limit the test-instance retrieved
 * @returns the requested test-instance
 */
export const getTestInstances: GetMany<BaseTestInstance> = async (params) =>
  (
    await ASSIST_API.get('test-instances', {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Fetch test-instance (singular)
 * @param params can be used to customise/limit the test-instance retrieved
 * @returns a single test-instance
 */
export const getTestInstance: GetOne<BaseTestInstance> = async (id, params) =>
  (
    await ASSIST_API.get(`test-instances/${id}`, {
      params: requestParams({ order, groups, include: includeExtended }, params)
    })
  ).data;

/**
 * Hook implementation to fetch test-instances
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns test-instances matching request
 */
export const useTestInstances: AssistQuery.UseMany<BaseTestInstance> = (params, options) =>
  useQueryAssist(['test-instances', params], () => getTestInstances(params), options);

/**
 * Given an testInstanceId fetch and return the area
 *  - only runs once testInstanceId is provided
 *  - fetches the one testInstance
 *  - caches data
 * @param id testInstanceId
 * @param params optional parameters to change requested data
 * @returns the matching testInstance
 */
export const useTestInstance: AssistQuery.UseOne<BaseTestInstance> = (id, params, options) =>
  useQueryAssist(['test-instances', { id, ...params }], () => getTestInstance(id, params), {
    ...options,
    enabled: !!id
  });

type PatchTestInstanceVariables = RecursivePartial<BaseTestInstance> & Pick<BaseTestInstance, 'id'>;

/**
 * Creates methods to send PATCH requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePatchTestInstance: AssistQuery.UseMutation<
  BaseTestInstance,
  PatchTestInstanceVariables
> = (options) =>
  useMutation(
    (testInstance: PatchTestInstanceVariables) =>
      ASSIST_API.patch(`test-instances/${testInstance.id}`, prepareTestInstance(testInstance), {
        params: requestParams({ order, groups, include })
      }),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['test-instances']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

type PostTestInstanceVariables = {
  customer: number;
  testInstance: RecursivePartial<BaseTestInstance>;
};

/**
 * Creates methods to send POST requests to the api
 *
 * mutate call will require a customer ID
 * so the testinstance can be linked to the correct school
 *
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePostTestInstance: AssistQuery.UseMutation<
  BaseTestInstance,
  PostTestInstanceVariables
> = (options) =>
  useMutation(
    ({ customer, testInstance }: PostTestInstanceVariables) =>
      ASSIST_API.post('test-instances', prepareTestInstance(testInstance, customer), {
        params: requestParams({ order, groups, include })
      }),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['test-instances']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

/**
 * Creates methods to send DELETE requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const useDeleteTestInstance: AssistQuery.UseMutation<BaseTestInstance, number> = (options) =>
  useMutation((id) => ASSIST_API.delete(`test-instances/${id}`), {
    ...options,
    onSettled: (...args) => {
      AssistQueryClient.invalidateQueries(['test-instances']);
      if (options?.onSettled) {
        options.onSettled(...args);
      }
    }
  });
